/**
 * Section A — the fixed compliance checklist, grouped by check_schema category, always in
 * the same order (CONVENTIONS.md: "the check grid is fixed"). Each row expands to its
 * reason + page reference; the reference jumps the correct document (the report, or the
 * LOI for instruction-match checks — see reviewer/types.ts#checkTargetDoc).
 */
import { checkTargetDoc } from '../../reviewer/types';
import type { CheckResultRow, CheckSchemaRow, CheckStatus } from '../../reviewer/types';
import { PageLink } from './PageLink';
import { Tile, Chip } from '../ui';

const STATUS_META: Record<CheckStatus, { glyph: string; label: string; tone: string }> = {
  pass: { glyph: '✓', label: 'Pass', tone: 'pass' },
  fail: { glyph: '✗', label: 'Fail', tone: 'fail' },
  flag: { glyph: '!', label: 'Flag', tone: 'flag' },
  na: { glyph: '–', label: 'N/A', tone: 'na' },
};

function stripBaselineSuffix(reason: string): string {
  return reason.replace(/\s*\[Institute baseline used:[^\]]*\]\s*$/, '').trim();
}

interface ChecklistSectionProps {
  schema: CheckSchemaRow[];
  results: CheckResultRow[];
  standingInstructionLabel: string;
  checked: boolean;
  onJump: (doc: 'report' | 'loi', page: number) => void;
}

export function ChecklistSection({ schema, results, standingInstructionLabel, checked, onJump }: ChecklistSectionProps) {
  const byKey = new Map(results.map((r) => [r.checkKey, r]));
  const categories: { category: string; items: CheckSchemaRow[] }[] = [];
  for (const item of schema) {
    let group = categories.find((c) => c.category === item.category);
    if (!group) {
      group = { category: item.category, items: [] };
      categories.push(group);
    }
    group.items.push(item);
  }

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">Compliance checklist</h2>
        <Chip>Standing instruction: {standingInstructionLabel}</Chip>
      </div>

      <div className="checklist-grid">
        {categories.map((group) => (
          <Tile key={group.category} className="checklist-cat">
            <h3 className="checklist-cat__title">{group.category}</h3>
            <div className="checklist-cat__rows">
              {group.items.map((item) => {
                const result = byKey.get(item.checkKey);
                const status = result?.status ?? null;
                const meta = status ? STATUS_META[status] : null;
                const doc = checkTargetDoc(item.category);
                return (
                  <details className="check-row" key={item.checkKey}>
                    <summary className="check-row__summary">
                      <span className={`mark mark--${meta ? meta.tone : 'na'}`} aria-hidden="true">
                        {meta ? meta.glyph : '–'}
                      </span>
                      <span className="check-row__label">{item.label}</span>
                    </summary>
                    <div className="check-row__body">
                      {result ? (
                        <>
                          <p className="check-row__reason">{stripBaselineSuffix(result.reason ?? '')}</p>
                          <PageLink page={result.pageRef} doc={doc} onJump={onJump} />
                          {result.evidenceSnippet ? (
                            <p className="check-row__evidence">“{result.evidenceSnippet}”</p>
                          ) : null}
                        </>
                      ) : (
                        <p className="muted">{checked ? 'Not applicable to this report.' : 'Awaiting the compliance check.'}</p>
                      )}
                    </div>
                  </details>
                );
              })}
            </div>
          </Tile>
        ))}
      </div>
    </section>
  );
}
